import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
    FaCalendarWeek, FaFilter, FaEdit, FaTrash, FaCheckCircle, 
    FaBell, FaClock, FaCalendarAlt, FaFlag, FaSort, FaSortUp, 
    FaSortDown, FaChevronLeft, FaChevronRight, FaSearch, 
    FaTimes, FaCheckDouble, FaDownload, FaEye, FaUserCheck, FaBuilding
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const UpcomingTasks = () => {
    const { user } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTasks, setSelectedTasks] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [snoozeTaskId, setSnoozeTaskId] = useState(null);
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    
    // Sorting
    const [sortField, setSortField] = useState('due_date');
    const [sortOrder, setSortOrder] = useState('asc');
    
    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [departmentFilter, setDepartmentFilter] = useState('all');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    
    // Departments
    const [departments, setDepartments] = useState([]);

    // Helper function to format date to dd/mm/yyyy
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}/${month}/${year}`;
    };

    // Helper function to parse dd/mm/yyyy to Date object for comparison
    const parseDate = (dateString) => {
        if (!dateString) return null;
        const parts = dateString.split('/');
        if (parts.length === 3) {
            return new Date(parts[2], parts[1] - 1, parts[0]);
        }
        return new Date(dateString);
    };

    useEffect(() => {
        fetchUpcomingTasks();
        fetchDepartments();
    }, []);

    const fetchUpcomingTasks = async () => {
        try {
            const response = await axios.get('/api/tasks/upcoming');
            const tasksData = response.data.tasks || [];
            // Format dates for display
            const formattedTasks = tasksData.map(task => ({
                ...task,
                formatted_due_date: formatDate(task.due_date),
                due_date_obj: task.due_date ? new Date(task.due_date) : null,
                assigned_to_name: task.assigned_to_name || task.assigned_name || 'Unassigned'
            }));
            setTasks(formattedTasks);
        } catch (error) {
            toast.error('Failed to load upcoming tasks');
        } finally {
            setLoading(false);
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await axios.get('/api/departments');
            if (response.data.success) {
                setDepartments(response.data.departments || []);
            }
        } catch (error) {
            console.error('Failed to load departments');
        }
    };

    const updateTaskStatus = async (taskId, status) => {
        try {
            await axios.patch(`/api/tasks/${taskId}/status`, { status });
            toast.success(`Task marked as ${status}`);
            fetchUpcomingTasks();
            setSelectedTasks(new Set());
            setSelectAll(false);
        } catch (error) {
            toast.error('Failed to update task status');
        }
    };

    const deleteTask = async (taskId) => {
        if (window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
            try {
                await axios.delete(`/api/tasks/${taskId}`);
                toast.success('Task deleted successfully');
                fetchUpcomingTasks();
                setSelectedTasks(new Set());
                setSelectAll(false);
            } catch (error) {
                toast.error('Failed to delete task');
            }
        }
    };

    const bulkDelete = async () => {
        if (selectedTasks.size === 0) {
            toast.error('No tasks selected');
            return;
        }
        
        if (window.confirm(`Are you sure you want to delete ${selectedTasks.size} task(s)? This action cannot be undone.`)) {
            try {
                const promises = Array.from(selectedTasks).map(taskId => 
                    axios.delete(`/api/tasks/${taskId}`)
                );
                await Promise.all(promises);
                toast.success(`${selectedTasks.size} task(s) deleted successfully`);
                fetchUpcomingTasks();
                setSelectedTasks(new Set());
                setSelectAll(false);
            } catch (error) {
                toast.error('Failed to delete some tasks');
            }
        }
    };

    const bulkComplete = async () => {
        if (selectedTasks.size === 0) {
            toast.error('No tasks selected');
            return;
        }
        
        try {
            const promises = Array.from(selectedTasks).map(taskId => 
                axios.patch(`/api/tasks/${taskId}/status`, { status: 'Completed' })
            );
            await Promise.all(promises);
            toast.success(`${selectedTasks.size} task(s) marked as completed`);
            fetchUpcomingTasks();
            setSelectedTasks(new Set());
            setSelectAll(false);
        } catch (error) {
            toast.error('Failed to update some tasks');
        }
    };

    const editTask = (taskId) => {
        window.location.href = `/edit-task/${taskId}`;
    };

    const viewTaskDetails = (taskId) => {
        window.location.href = `/task/${taskId}`;
    };

    const snoozeTask = async (taskId, days) => {
        try {
            const task = tasks.find(t => t.task_id === taskId);
            if (!task) return;
            
            const newDueDate = new Date(task.due_date);
            newDueDate.setDate(newDueDate.getDate() + days);
            
            await axios.put(`/api/tasks/${taskId}`, {
                ...task,
                dueDate: newDueDate.toISOString().split('T')[0]
            });
            
            toast.success(`Task snoozed for ${days} day(s)`);
            setSnoozeTaskId(null);
            fetchUpcomingTasks();
        } catch (error) {
            toast.error('Failed to snooze task');
        }
    };

    const exportTasks = () => {
        if (filteredTasksLocal.length === 0) {
            toast.error('No tasks to export');
            return;
        }
        
        const exportData = filteredTasksLocal.map(task => ({
            'Task ID': task.task_id,
            'Title': task.title,
            'Description': task.description,
            'Assigned To': task.assigned_to_name,
            'Priority': task.priority,
            'Due Date': task.formatted_due_date,
            'Department': task.department,
            'Status': task.status
        }));
        
        const headers = Object.keys(exportData[0]).join(',');
        const rows = exportData.map(row => Object.values(row).map(value => `"${value || ''}"`).join(',')).join('\n');
        const csv = `${headers}\n${rows}`;
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `upcoming_tasks_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Tasks exported successfully');
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedTasks(new Set());
        } else {
            const newSelected = new Set(paginatedTasks.map(task => task.task_id));
            setSelectedTasks(newSelected);
        }
        setSelectAll(!selectAll);
    };

    const handleSelectTask = (taskId) => {
        const newSelected = new Set(selectedTasks);
        if (newSelected.has(taskId)) {
            newSelected.delete(taskId);
        } else {
            newSelected.add(taskId);
        }
        setSelectedTasks(newSelected);
        setSelectAll(newSelected.size === paginatedTasks.length && paginatedTasks.length > 0);
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
        setCurrentPage(1);
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return <FaSort className="text-gray-400" />;
        return sortOrder === 'asc' ? <FaSortUp className="text-purple-500" /> : <FaSortDown className="text-purple-500" />;
    };

    const resetFilters = () => {
        setSearchTerm('');
        setPriorityFilter('all');
        setDepartmentFilter('all');
        setDateRange({ start: '', end: '' });
        setCurrentPage(1);
    };

    const getPriorityColor = (priority) => {
        switch(priority) {
            case 'High': return 'priority-high';
            case 'Medium': return 'priority-medium';
            case 'Low': return 'priority-low';
            default: return 'priority-medium';
        }
    };

    // Get days until due date
    const getDaysUntilDue = (dueDate) => {
        if (!dueDate) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const due = new Date(dueDate);
        const diffTime = due - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    // Apply filters
    const filteredTasksLocal = tasks.filter(task => {
        if (!task) return false;
        
        const matchesSearch = (task.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (task.description?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (task.assigned_to_name?.toLowerCase() || '').includes(searchTerm.toLowerCase());
        
        let matchesPriority = true;
        if (priorityFilter !== 'all') {
            matchesPriority = (task.priority?.toLowerCase() || '') === priorityFilter.toLowerCase();
        }
        
        let matchesDepartment = true;
        if (departmentFilter !== 'all') {
            matchesDepartment = (task.department || '') === departmentFilter;
        }
        
        let matchesDateRange = true;
        if (dateRange.start && task.due_date) {
            const startDate = parseDate(dateRange.start);
            const taskDate = new Date(task.due_date);
            matchesDateRange = matchesDateRange && taskDate >= startDate;
        }
        if (dateRange.end && task.due_date) {
            const endDate = parseDate(dateRange.end);
            const taskDate = new Date(task.due_date);
            matchesDateRange = matchesDateRange && taskDate <= endDate;
        }
        
        return matchesSearch && matchesPriority && matchesDepartment && matchesDateRange;
    });

    // Apply sorting
    const sortedTasks = [...filteredTasksLocal].sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];
        
        if (sortField === 'due_date') {
            aVal = a.due_date_obj || new Date(0);
            bVal = b.due_date_obj || new Date(0);
        } else if (sortField === 'assigned_to_name') {
            aVal = a.assigned_to_name || '';
            bVal = b.assigned_to_name || '';
        }
        
        if (typeof aVal === 'string') {
            aVal = aVal.toLowerCase();
            bVal = bVal.toLowerCase();
        }
        
        if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
        return 0;
    });

    // Pagination
    const totalPages = Math.ceil(sortedTasks.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedTasks = sortedTasks.slice(startIndex, startIndex + itemsPerPage);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <div className="loader"></div>
            </div>
        );
    }

    const isAdmin = user?.role === 'Admin';

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-2">
                        <FaCalendarWeek className="text-purple-500" />
                        Upcoming Tasks
                    </h1>
                    <p className="text-gray-500 mt-1">Tasks due in the next 7 days</p>
                </div>
                <div className="flex gap-3 flex-wrap">
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                            showFilters ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        <FaFilter /> Filters
                    </button>
                    <button
                        onClick={exportTasks}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all flex items-center gap-2"
                    >
                        <FaDownload /> Export
                    </button>
                    {selectedTasks.size > 0 && (
                        <>
                            <button
                                onClick={bulkComplete}
                                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all flex items-center gap-2"
                            >
                                <FaCheckDouble /> Complete ({selectedTasks.size})
                            </button>
                            <button
                                onClick={bulkDelete}
                                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all flex items-center gap-2"
                            >
                                <FaTrash /> Delete ({selectedTasks.size})
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
                <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Search</label>
                            <div className="relative">
                                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by title, description or assignee..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
                            <select
                                value={priorityFilter}
                                onChange={(e) => setPriorityFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                                <option value="all">All Priorities</option>
                                <option value="high">High</option>
                                <option value="medium">Medium</option>
                                <option value="low">Low</option>
                            </select>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Department</label>
                            <select
                                value={departmentFilter}
                                onChange={(e) => setDepartmentFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                                <option value="all">All Departments</option>
                                {departments.map(dept => (
                                    <option key={dept} value={dept}>{dept}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Due Date From (DD/MM/YYYY)</label>
                            <input
                                type="text"
                                placeholder="DD/MM/YYYY"
                                value={dateRange.start}
                                onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Due Date To (DD/MM/YYYY)</label>
                            <input
                                type="text"
                                placeholder="DD/MM/YYYY"
                                value={dateRange.end}
                                onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        
                        <div className="flex items-end">
                            <button
                                onClick={resetFilters}
                                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all flex items-center gap-2"
                            >
                                <FaTimes /> Reset Filters
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Results Summary */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                <p className="text-sm text-gray-500">
                    Showing {sortedTasks.length > 0 ? startIndex + 1 : 0} to {Math.min(startIndex + itemsPerPage, sortedTasks.length)} of {sortedTasks.length} upcoming tasks
                </p>
                <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-500">Show:</label>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                        className="px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
            </div>

            {/* Tasks Table */}
            {paginatedTasks.length > 0 ? (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 w-10">
                                        <input
                                            type="checkbox"
                                            checked={selectAll && paginatedTasks.length > 0}
                                            onChange={handleSelectAll}
                                            className="w-4 h-4 rounded border-gray-300 focus:ring-purple-500"
                                        />
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('title')}>
                                        <div className="flex items-center gap-1">Task {getSortIcon('title')}</div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('assigned_to_name')}>
                                        <div className="flex items-center gap-1">Assigned To {getSortIcon('assigned_to_name')}</div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('due_date')}>
                                        <div className="flex items-center gap-1">Due Date {getSortIcon('due_date')}</div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('priority')}>
                                        <div className="flex items-center gap-1">Priority {getSortIcon('priority')}</div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {paginatedTasks.map(task => {
                                    const daysUntil = getDaysUntilDue(task.due_date);
                                    return (
                                        <tr key={task.task_id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-4">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTasks.has(task.task_id)}
                                                    onChange={() => handleSelectTask(task.task_id)}
                                                    className="w-4 h-4 rounded border-gray-300 focus:ring-purple-500"
                                                />
                                            </td>
                                            <td className="px-4 py-4">
                                                <div>
                                                    <div className="font-medium text-gray-900">{task.title}</div>
                                                    <div className="text-sm text-gray-500 mt-1 max-w-md truncate">{task.description}</div>
                                                    {task.department && (
                                                        <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                                            <FaBuilding /> {task.department}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                                        <FaUserCheck className="text-purple-600 text-sm" />
                                                    </div>
                                                    <div>
                                                        <div className="font-medium text-gray-900">
                                                            {task.assigned_to_name || 'Unassigned'}
                                                        </div>
                                                        {task.assigned_mobile && (
                                                            <div className="text-xs text-gray-400">{task.assigned_mobile}</div>
                                                        )}
                                                    </div>
                                                </div>
                                             </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-1">
                                                        <FaCalendarAlt className="text-gray-400" />
                                                        <span className="text-gray-600 font-medium">{task.formatted_due_date}</span>
                                                    </div>
                                                    {daysUntil !== null && daysUntil <= 2 && daysUntil >= 0 && (
                                                        <span className="text-xs text-red-500 mt-1 font-semibold">
                                                            Due soon! ({daysUntil} day{daysUntil !== 1 ? 's' : ''} left)
                                                        </span>
                                                    )}
                                                    {daysUntil !== null && daysUntil < 0 && (
                                                        <span className="text-xs text-red-600 mt-1 font-semibold">
                                                            Overdue by {Math.abs(daysUntil)} day{Math.abs(daysUntil) !== 1 ? 's' : ''}
                                                        </span>
                                                    )}
                                                </div>
                                             </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <span className={getPriorityColor(task.priority)}>
                                                    <FaFlag className="inline mr-1" size={12} /> {task.priority}
                                                </span>
                                             </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-gray-600">
                                                {task.department || '-'}
                                             </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => updateTaskStatus(task.task_id, 'Completed')}
                                                        className="p-1 text-green-600 hover:text-green-800 transition-colors"
                                                        title="Mark Complete"
                                                    >
                                                        <FaCheckCircle size={18} />
                                                    </button>
                                                    <div className="relative">
                                                        <button
                                                            onClick={() => setSnoozeTaskId(snoozeTaskId === task.task_id ? null : task.task_id)}
                                                            className="p-1 text-orange-600 hover:text-orange-800 transition-colors"
                                                            title="Snooze"
                                                        >
                                                            <FaBell size={18} />
                                                        </button>
                                                        {snoozeTaskId === task.task_id && (
                                                            <div className="absolute top-full mt-1 right-0 bg-white rounded-lg shadow-lg p-2 z-10 min-w-[120px] border border-gray-200">
                                                                {[1, 2, 3, 5, 7].map(days => (
                                                                    <button
                                                                        key={days}
                                                                        onClick={() => snoozeTask(task.task_id, days)}
                                                                        className="w-full px-3 py-1 text-left hover:bg-gray-100 rounded text-sm"
                                                                    >
                                                                        {days} day{days > 1 ? 's' : ''}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button
                                                        onClick={() => editTask(task.task_id)}
                                                        className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                                                        title="Edit"
                                                    >
                                                        <FaEdit size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => viewTaskDetails(task.task_id)}
                                                        className="p-1 text-purple-600 hover:text-purple-800 transition-colors"
                                                        title="View Details"
                                                    >
                                                        <FaEye size={18} />
                                                    </button>
                                                    {isAdmin && (
                                                        <button
                                                            onClick={() => deleteTask(task.task_id)}
                                                            className="p-1 text-red-600 hover:text-red-800 transition-colors"
                                                            title="Delete"
                                                        >
                                                            <FaTrash size={18} />
                                                        </button>
                                                    )}
                                                </div>
                                             </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 px-6 py-4 border-t">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 bg-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-300 transition-colors flex items-center gap-1"
                            >
                                <FaChevronLeft /> Previous
                            </button>
                            <div className="flex gap-2 flex-wrap justify-center">
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNum = totalPages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`px-3 py-1 rounded-lg transition-colors ${
                                                currentPage === pageNum
                                                    ? 'bg-purple-500 text-white'
                                                    : 'bg-gray-200 hover:bg-gray-300'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 bg-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-300 transition-colors flex items-center gap-1"
                            >
                                Next <FaChevronRight />
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <FaCalendarWeek className="mx-auto text-5xl text-gray-300 mb-3" />
                    <p className="text-gray-500">No upcoming tasks found</p>
                    <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or check back later</p>
                    {(searchTerm || priorityFilter !== 'all' || departmentFilter !== 'all' || dateRange.start || dateRange.end) && (
                        <button
                            onClick={resetFilters}
                            className="mt-4 text-purple-500 hover:text-purple-600 flex items-center gap-2 mx-auto"
                        >
                            <FaTimes /> Clear all filters
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default UpcomingTasks;